// Safe evaluator for `explore` block expressions: y = f(x).
//
// Recursive descent over a whitelisted grammar — numbers, x/pi/e, + - * / ^,
// parens, and a fixed function set. Never eval() or new Function(): lesson
// JSON is model output, and this is the only place it approaches execution.
// The backend validates the same grammar at generation time; this mirror
// exists so a hand-edited or legacy document still can't escape.

const FUNCS = {
  sin: Math.sin, cos: Math.cos, tan: Math.tan, exp: Math.exp,
  log: Math.log, sqrt: Math.sqrt, abs: Math.abs,
  min: (...a) => Math.min(...a), max: (...a) => Math.max(...a),
};
const CONSTS = { pi: Math.PI, e: Math.E };

// Compile expr into f(x), or return null if the grammar is violated.
export function compileExpr(expr) {
  if (typeof expr !== "string" || !expr.trim() || expr.length > 120) return null;
  const src = expr.replace(/\s+/g, "");
  let pos = 0;

  const peek = () => src[pos];
  const eat = (ch) => { if (src[pos] !== ch) throw new Error(`expected ${ch}`); pos++; };

  function expression() {
    let node = term();
    while (peek() === "+" || peek() === "-") {
      const op = src[pos++]; const rhs = term();
      const lhs = node;
      node = op === "+" ? (x) => lhs(x) + rhs(x) : (x) => lhs(x) - rhs(x);
    }
    return node;
  }
  function term() {
    let node = factor();
    while (peek() === "*" || peek() === "/") {
      const op = src[pos++]; const rhs = factor();
      const lhs = node;
      node = op === "*" ? (x) => lhs(x) * rhs(x) : (x) => lhs(x) / rhs(x);
    }
    return node;
  }
  // Pow binds tighter than unary minus (Python semantics, matching the
  // backend validator): -x^2 is -(x^2), and ^ is right-associative.
  function factor() {
    if (peek() === "-") { pos++; const inner = factor(); return (x) => -inner(x); }
    if (peek() === "+") { pos++; return factor(); }
    return power();
  }
  function power() {
    const base = primary();
    if (peek() === "^") { pos++; const exp2 = factor(); return (x) => Math.pow(base(x), exp2(x)); }
    return base;
  }
  function primary() {
    const ch = peek();
    if (ch === "(") { eat("("); const inner = expression(); eat(")"); return inner; }
    if (/[0-9.]/.test(ch)) {
      const m = /^\d*\.?\d+(?:[eE][+-]?\d+)?/.exec(src.slice(pos));
      if (!m) throw new Error("bad number");
      pos += m[0].length;
      const v = parseFloat(m[0]);
      return () => v;
    }
    const nm = /^[a-z]+/.exec(src.slice(pos));
    if (!nm) throw new Error(`unexpected ${ch}`);
    const name = nm[0];
    pos += name.length;
    if (peek() === "(") {
      const fn = FUNCS[name];
      if (!fn) throw new Error(`unknown function ${name}`);
      eat("(");
      const args = [expression()];
      while (peek() === ",") { pos++; args.push(expression()); }
      eat(")");
      return (x) => fn(...args.map((a) => a(x)));
    }
    if (name === "x") return (x) => x;
    if (name in CONSTS) { const v = CONSTS[name]; return () => v; }
    throw new Error(`unknown name ${name}`);
  }

  try {
    const f = expression();
    if (pos !== src.length) return null;
    const probe = f(1);
    if (typeof probe !== "number") return null;
    return f;
  } catch {
    return null;
  }
}

// Sample n points of f across [xMin, xMax]; non-finite ys become null (gaps).
export function sampleExpr(f, xMin, xMax, n = 120) {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const x = xMin + ((xMax - xMin) * i) / (n - 1);
    const y = f(x);
    pts.push({ x, y: Number.isFinite(y) ? y : null });
  }
  return pts;
}
